import { BulkUpdateFieldType } from "components/BulkUpdateOptionalFieldsModal/types";
import {
  DatePicker,
  Input,
  OptionTogglerField,
  SelectField,
  TextArea,
  withNamespace,
} from "components/Form/FormikControls";
import { SellSafeOptionToggler } from "components/SellSafe/SellSafe";
import InlineConsignmentSelect from "components/SaleLotForms/InlineConsignmentSelect";
import AuctionsPlusClearingSaleDescriptionForm from "components/GeneralSaleLotForm/AuctionsPlusClearingSaleDescriptionForm";
import { ExportSitesField } from "components/BulkUpdateOptionalFieldsModal/ClearingSaleLots/ExportSitesField";
import {
  ItemCondition,
  LotStatusAsOptions,
  NewUsed,
} from "constants/clearingSaleAttributes";
import { SaleLotNamespace } from "constants/saleLots";
import { CollapseTitle } from "components/BulkUpdateOptionalFieldsModal/util";
import { UpdateInformation } from "components/BulkUpdateOptionalFieldsModal/UpdateInformationBase";
import { labelFields } from "components/BulkUpdateOptionalFieldsModal/SaleLots/Fields";

// The plain `labels` field is read-only on the bulk_update endpoint. Bulk
// label edits must go through the Master Labels mechanism instead. Reuse
// Livestock's field config, but group it under General.
const clearingSaleLabelFields: BulkUpdateFieldType[] = labelFields.map(field => ({
  ...field,
  collapseTitle: CollapseTitle.GENERAL,
}));

const NAMESPACE = SaleLotNamespace.CLEARING_SALE_ATTRIBUTES;

// Fields are grouped to match the sections of the real Clearing Sale lot
// edit modal (DetailedSaleLotModal). Keep this grouping. Do not reorganise
// by field type. Field order inside each group also matches the real form.
// Do not alphabetize.

// Matches the "General" section of DetailedSaleLotModal (GeneralCollapse).
export const clearingSaleGeneralFields: BulkUpdateFieldType[] = [
  {
    fieldName: "consignment_id",
    Component: InlineConsignmentSelect,
    componentProps: {
      label: "Vendor",
    },
    collapseTitle: CollapseTitle.GENERAL,
    UpdateInformationComponent: UpdateInformation,
    emptyValue: null,
  },
  {
    fieldName: withNamespace(NAMESPACE, "status"),
    Component: SelectField,
    componentProps: {
      label: "Status",
      options: LotStatusAsOptions,
      isClearable: true,
    },
    collapseTitle: CollapseTitle.GENERAL,
    UpdateInformationComponent: UpdateInformation,
    emptyValue: null,
  },
  {
    fieldName: "quantity",
    Component: Input,
    componentProps: {
      // Clearing Sales show the "Head Count" field as "Quantity/Units".
      // See SaleTypeVocabulary[SaleType.CLEARING] in constants/sale.ts.
      label: "Quantity/Units",
      type: "number",
    },
    collapseTitle: CollapseTitle.GENERAL,
    UpdateInformationComponent: UpdateInformation,
    emptyValue: 0,
  },
  ...clearingSaleLabelFields,
  {
    fieldName: withNamespace(NAMESPACE, "publicDescription"),
    Component: TextArea,
    componentProps: {
      label: "Public Description",
    },
    collapseTitle: CollapseTitle.GENERAL,
    UpdateInformationComponent: UpdateInformation,
    emptyValue: "",
  },
  {
    fieldName: withNamespace(NAMESPACE, "comments"),
    Component: TextArea,
    componentProps: {
      label: "Comments",
    },
    collapseTitle: CollapseTitle.GENERAL,
    UpdateInformationComponent: UpdateInformation,
    emptyValue: "",
  },
  {
    fieldName: "note",
    Component: TextArea,
    componentProps: {
      label: "Notes",
    },
    collapseTitle: CollapseTitle.GENERAL,
    UpdateInformationComponent: UpdateInformation,
    emptyValue: "",
  },
  {
    fieldName: withNamespace(NAMESPACE, "inspection"),
    Component: TextArea,
    componentProps: {
      label: "Inspection",
    },
    collapseTitle: CollapseTitle.GENERAL,
    UpdateInformationComponent: UpdateInformation,
    emptyValue: "",
  },
  {
    fieldName: withNamespace(NAMESPACE, "newUsed"),
    Component: OptionTogglerField,
    componentProps: {
      label: "New/Used",
      labelPosition: "top",
      options: Object.values(NewUsed),
      allowToggleOff: true,
    },
    collapseTitle: CollapseTitle.GENERAL,
    UpdateInformationComponent: UpdateInformation,
    emptyValue: null,
  },
  {
    fieldName: withNamespace(NAMESPACE, "year"),
    Component: DatePicker,
    componentProps: {
      label: "Year",
      views: ["year"],
      openTo: "year",
    },
    collapseTitle: CollapseTitle.GENERAL,
    UpdateInformationComponent: UpdateInformation,
    emptyValue: null,
  },
  {
    fieldName: withNamespace(NAMESPACE, "itemCondition"),
    Component: OptionTogglerField,
    componentProps: {
      label: "Condition",
      labelPosition: "top",
      options: Object.values(ItemCondition),
      allowToggleOff: true,
    },
    collapseTitle: CollapseTitle.GENERAL,
    UpdateInformationComponent: UpdateInformation,
    emptyValue: null,
  },
  {
    fieldName: withNamespace(NAMESPACE, "sellSafeCategory"),
    Component: SellSafeOptionToggler,
    componentProps: {},
    collapseTitle: CollapseTitle.GENERAL,
    UpdateInformationComponent: UpdateInformation,
    emptyValue: null,
  },
  {
    fieldName: withNamespace(NAMESPACE, "category"),
    // Category, Type, and Sub Type are cascading fields. The chosen Category
    // limits the Type options. The chosen Type limits the Sub Type options.
    // This component already manages that cascade for the single-lot form.
    // Reuse it here instead of building the cascade again.
    updateFieldNames: [
      withNamespace(NAMESPACE, "category"),
      withNamespace(NAMESPACE, "itemType"),
      withNamespace(NAMESPACE, "subType"),
    ],
    Component: AuctionsPlusClearingSaleDescriptionForm,
    componentProps: {
      namespace: NAMESPACE,
    },
    collapseTitle: CollapseTitle.GENERAL,
    UpdateInformationComponent: UpdateInformation,
    emptyValue: null,
  },
  {
    fieldName: withNamespace(NAMESPACE, "ppsrStatus"),
    Component: Input,
    componentProps: {
      label: "PPSR Status",
    },
    collapseTitle: CollapseTitle.GENERAL,
    UpdateInformationComponent: UpdateInformation,
    emptyValue: "",
  },
  {
    fieldName: withNamespace(NAMESPACE, "ppsrComments"),
    Component: TextArea,
    componentProps: {
      label: "PPSR Comments",
    },
    collapseTitle: CollapseTitle.GENERAL,
    UpdateInformationComponent: UpdateInformation,
    emptyValue: "",
  },
];

// Matches the "Integrations" section of DetailedSaleLotModal (IntegrationCollapse).
export const clearingSaleIntegrationsFields: BulkUpdateFieldType[] = [
  {
    fieldName: "exportSites",
    Component: ExportSitesField,
    componentProps: {
      label: "Export To",
    },
    collapseTitle: CollapseTitle.INTEGRATIONS,
    UpdateInformationComponent: UpdateInformation,
    emptyValue: [],
  },
];
